import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { format } from 'date-fns';
import AddMaintenanceLogDialog from './AddMaintenanceLogDialog';
import EditMaintenanceLogDialog from './EditMaintenanceLogDialog';
import DeleteMaintenanceLogDialog from './DeleteMaintenanceLogDialog';

const MaintenanceLogList = ({ droneId, organizationUuid, droneName }) => {
  const [maintenanceLogs, setMaintenanceLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const { getAccessTokenSilently } = useAuth0();

  const fetchMaintenanceLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      const url = `${process.env.REACT_APP_API_URL}/api/organization/${organizationUuid}/drones/${droneId}/maintenance`;


      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });


      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to fetch maintenance logs (${response.status})`);
      }

      const data = await response.json();
      setMaintenanceLogs(data.maintenanceLogs || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (droneId && organizationUuid) {
      fetchMaintenanceLogs();
    }
  }, [droneId, organizationUuid]);

  const handleAddSuccess = () => {
    setOpenAddDialog(false);
    fetchMaintenanceLogs();
  };

  const handleEditSuccess = () => {
    setOpenEditDialog(false);
    setSelectedLog(null);
    fetchMaintenanceLogs();
  };

  const handleDeleteSuccess = () => {
    setOpenDeleteDialog(false);
    setSelectedLog(null);
    fetchMaintenanceLogs();
  };

  const getMaintenanceTypeColor = (type) => {
    const colors = {
      routine: 'info',
      repair: 'warning',
      inspection: 'default',
      calibration: 'primary',
      replacement: 'error',
      upgrade: 'success'
    };
    return colors[type] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <BuildIcon color="primary" />
          <Typography variant="h6">
            Maintenance History
          </Typography>
          {droneName && (
            <Typography variant="body2" color="textSecondary">
              - {droneName}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddDialog(true)}
          size="small"
        >
          Add Maintenance Log
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell><strong>Performed By</strong></TableCell>
              <TableCell><strong>Cost</strong></TableCell>
              <TableCell><strong>Next Due</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {maintenanceLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary" py={3}>
                    No maintenance logs found. Click "Add Maintenance Log" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              maintenanceLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>{formatDate(log.performedDate)}</TableCell>
                  <TableCell>
                    <Chip
                      label={log.maintenanceType}
                      color={getMaintenanceTypeColor(log.maintenanceType)}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={log.description}>
                      <Typography variant="body2" noWrap maxWidth={200}>
                        {log.description}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {log.performer ?
                      `${log.performer.firstName} ${log.performer.lastName}` :
                      'Unknown'}
                  </TableCell>
                  <TableCell>
                    {formatCurrency(log.cost, log.costCurrency)}
                  </TableCell>
                  <TableCell>
                    {log.nextMaintenanceDate ? (
                      <Typography
                        variant="body2"
                        color={new Date(log.nextMaintenanceDate) < new Date() ? 'error' : 'textPrimary'}
                      >
                        {formatDate(log.nextMaintenanceDate)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Not set
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      title="Edit"
                      onClick={() => {
                        setSelectedLog(log);
                        setOpenEditDialog(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      title="Delete"
                      onClick={() => {
                        setSelectedLog(log);
                        setOpenDeleteDialog(true);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Maintenance Log Dialog */}
      <AddMaintenanceLogDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onSuccess={handleAddSuccess}
        droneId={droneId}
        organizationUuid={organizationUuid}
        droneName={droneName}
      />

      {/* Edit Maintenance Log Dialog */}
      {selectedLog && (
        <EditMaintenanceLogDialog
          open={openEditDialog}
          onClose={() => {
            setOpenEditDialog(false);
            setSelectedLog(null);
          }}
          onSuccess={handleEditSuccess}
          droneId={droneId}
          organizationUuid={organizationUuid}
          droneName={droneName}
          logId={selectedLog.id}
        />
      )}

      {/* Delete Maintenance Log Dialog */}
      {selectedLog && (
        <DeleteMaintenanceLogDialog
          open={openDeleteDialog}
          onClose={() => {
            setOpenDeleteDialog(false);
            setSelectedLog(null);
          }}
          onSuccess={handleDeleteSuccess}
          droneId={droneId}
          organizationUuid={organizationUuid}
          droneName={droneName}
          maintenanceLog={selectedLog}
        />
      )}
    </Box>
  );
};

export default MaintenanceLogList;
